import { CreateSimulationGroupRequest, Simulation, SimulationGroup } from '@thive/fddm-types';
import { getConfig } from '@/modules/common/helpers/config';
import { API_TIMEOUTS } from '../constant';
import { ISimulationServiceConnector } from '@/modules/api/SimulationServiceConnector/ISimulationServiceConnector';
import { downloadFile } from '@/modules/common/helpers/browser';
import { BaseConnector } from '@/modules/api/BaseConnector';

export class SimulationServiceConnector
  extends BaseConnector
  implements ISimulationServiceConnector
{
  private static instance: SimulationServiceConnector;
  private static baseUrl = getConfig().api.simulation.url;
  private static scopes = [getConfig().api.simulation.scope];

  public static getInstance(): ISimulationServiceConnector {
    if (!SimulationServiceConnector.instance) {
      SimulationServiceConnector.instance = new SimulationServiceConnector();
    }
    return SimulationServiceConnector.instance;
  }

  private constructor() {
    super(
      SimulationServiceConnector.baseUrl,
      SimulationServiceConnector.scopes,
      API_TIMEOUTS.simulation,
    );
  }

  async getSimulationGroups(projectId: string, alternativeId: string): Promise<SimulationGroup[]> {
    return (
      (await this.get<SimulationGroup[]>(
        `/${projectId}/alternatives/${alternativeId}/simulationGroups`,
      )) ?? []
    );
  }

  async getSimulationGroup(
    projectId: string,
    alternativeId: string,
    simulationGroupId: string,
  ): Promise<SimulationGroup> {
    return this.get<SimulationGroup>(
      `/${projectId}/alternatives/${alternativeId}/simulationGroups/${simulationGroupId}`,
      {
        requiredErrorMessage: `Failed to get simulation group ${simulationGroupId} for projectId ${projectId} and alternativeId ${alternativeId}`,
      },
    );
  }

  async getSimulationsInGroup(
    projectId: string,
    alternativeId: string,
    simulationGroupId: string,
  ): Promise<Simulation[]> {
    return (
      (await this.get<Simulation[]>(
        `/${projectId}/alternatives/${alternativeId}/simulationGroups/${simulationGroupId}/simulations`,
      )) ?? []
    );
  }

  async createSimulationGroup(
    projectId: string,
    alternativeId: string,
    request: CreateSimulationGroupRequest,
  ): Promise<SimulationGroup> {
    return this.post<SimulationGroup, CreateSimulationGroupRequest>(
      `/${projectId}/alternatives/${alternativeId}/simulationGroups`,
      request,
      {
        requiredErrorMessage: `Failed to create simulation group for projectId ${projectId} and alternativeId ${alternativeId}`,
      },
    );
  }

  async renameSimulationGroup(
    projectId: string,
    alternativeId: string,
    simulationGroupId: string,
    newName: string,
  ): Promise<SimulationGroup> {
    return this.put<SimulationGroup, { name: string }>(
      `/${projectId}/alternatives/${alternativeId}/simulationGroups/${simulationGroupId}/name`,
      { name: newName },
      {
        requiredErrorMessage: `Failed to rename simulation group ${simulationGroupId} for projectId ${projectId} and alternativeId ${alternativeId}`,
      },
    );
  }

  async reRunSimulationGroup(
    projectId: string,
    alternativeId: string,
    simulationGroupId: string,
  ): Promise<SimulationGroup> {
    return this.post<SimulationGroup>(
      `/${projectId}/alternatives/${alternativeId}/simulationGroups/${simulationGroupId}/rerun`,
      undefined,
      {
        requiredErrorMessage: `Failed to re-run simulation group ${simulationGroupId} for projectId ${projectId} and alternativeId ${alternativeId}`,
      },
    );
  }

  async deleteSimulationGroup(
    projectId: string,
    alternativeId: string,
    simulationGroupId: string,
  ): Promise<void> {
    await this.delete<void>(
      `/${projectId}/alternatives/${alternativeId}/simulationGroups/${simulationGroupId}`,
    );
  }

  async abortSimulationGroup(
    projectId: string,
    alternativeId: string,
    simulationGroupId: string,
  ): Promise<void> {
    await this.post<void>(
      `/${projectId}/alternatives/${alternativeId}/simulationGroups/${simulationGroupId}/abort`,
    );
  }

  async downloadSimulationFiles(
    projectId: string,
    simulationRunId: string,
    alternativeName: string,
    simulationName: string,
  ): Promise<void> {
    const data = await this.get<Blob>(
      `/${projectId}/simulations/${simulationRunId}/downloadSimulationFiles`,
      {
        requiredErrorMessage: `Error while downloading simulation files for simulation with simulationRunId ${simulationRunId} in projectId ${projectId}`,
        skipLatest: true,
        config: {
          responseType: 'blob',
        },
      },
    );

    const fileName = `simulationFiles_${alternativeName}_${simulationName}_${simulationRunId}`;
    downloadFile(data, 'application/zip', fileName);
  }

  async downloadFmsConfig(
    projectId: string,
    alternativeId: string,
    request: CreateSimulationGroupRequest,
  ): Promise<void> {
    const data = await this.post<Blob>(
      `/${projectId}/alternatives/${alternativeId}/downloadFMSConfig`,
      request,
      {
        requiredErrorMessage: `Error while downloading fms config for alternative floor plan ${alternativeId} and projectId ${projectId}`,
        skipLatest: true,
        config: {
          responseType: 'blob',
        },
      },
    );

    const jsonData = await data.text();
    const prettyJson = JSON.stringify(JSON.parse(jsonData), null, 2);

    const blob = new Blob([prettyJson], { type: 'application/json' });
    downloadFile(blob, '.json', `FMSConfig-${alternativeId}.json`);
  }
}
